import { defineStore } from 'pinia'
import { noAuthApi } from '@/services/axiosApi'

declare const Echo: {
  channel: (channel: string) => {
    listen: (event: string, callback: (data: any) => void) => ReturnType<typeof Echo.channel>
  }
}

export const useSystemStore = defineStore('system', () => {
  const newVersionAvailable = ref(false)
  const maintenanceMode = ref(false)
  const maintenanceMessage = ref<string | null>(null)
  const maintenanceEta = ref<string | null>(null)

  let pollingInterval: ReturnType<typeof setInterval> | null = null
  let initialized = false
  let lastKnownDeployedAt: number | null = null

  function setMaintenance(active: boolean, message?: string | null, eta?: string | null) {
    maintenanceMode.value = active
    maintenanceMessage.value = message ?? null
    maintenanceEta.value = eta ?? null

    // If maintenance just ended and a new version is available, reload
    if (!active && newVersionAvailable.value) {
      reloadApp()
    }
  }

  function initSystemChannel() {
    Echo.channel('rm-system')
      .listen('AppVersionChanged', () => {
        newVersionAvailable.value = true
      })
      .listen('MaintenanceModeChanged', (data: { active: boolean; message?: string; estimatedEnd?: string }) => {
        setMaintenance(data.active, data.message, data.estimatedEnd)
      })
  }

  async function fetchVersion() {
    const { data } = await noAuthApi.get('/api/version')

    if (!initialized) {
      initialized = true
      lastKnownDeployedAt = data.deployed_at
    } else if (data.deployed_at !== null && data.deployed_at !== lastKnownDeployedAt) {
      newVersionAvailable.value = true
      lastKnownDeployedAt = data.deployed_at
    }

    if (data.maintenance) {
      setMaintenance(true, data.maintenanceMessage, data.maintenanceEta)
    } else if (maintenanceMode.value) {
      setMaintenance(false)
    }
  }

  function startVersionPolling() {
    // Skip polling in dev mode — HMR handles reloads
    if (import.meta.env.DEV) return

    // Immediate fetch to initialize lastKnownDeployedAt
    fetchVersion().catch(() => {})

    pollingInterval = setInterval(async () => {
      try {
        await fetchVersion()
      } catch {
        // Silently fail — API may be temporarily unavailable during deploy
      }
    }, 5 * 60 * 1000) // 5 minutes
  }

  function stopVersionPolling() {
    if (pollingInterval) {
      clearInterval(pollingInterval)
      pollingInterval = null
    }
  }

  function reloadApp() {
    window.location.reload()
  }

  return {
    newVersionAvailable,
    maintenanceMode,
    maintenanceMessage,
    maintenanceEta,
    setMaintenance,
    initSystemChannel,
    startVersionPolling,
    stopVersionPolling,
    reloadApp,
  }
})
